import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useConfirm } from 'src/hooks';
import { isAdminSelector } from 'src/utils/helper';
import homeworkReplyCommentActions, {
  selectHomeworkReplyComments,
} from 'src/store/model/homework_reply_comment';
import { Comments } from 'src/components/coreUI/index';
import {
  deleteHomeworkComment,
  updateHomeworkComment,
} from 'src/thunks/homework_comment';
import ChatBox from 'src/components/custom/Editor/ChatBox';
import homeworkCommentActions from 'src/store/model/homework_comment';
import { htmlParser } from 'src/libs/htmlParser';
import {
  createHomeworkCommentReply,
  deleteHomeworkCommentReply,
  fetchHomeworkCommentReply,
  updateHomeworkCommentReply,
} from 'src/thunks/homework_reply_comment';
import { useLocation, useNavigate } from 'react-router-dom';

const HomeworkReply: React.FC<Model.HomeworkComment> = (props) => {
  const {
    user,
    created_at,
    message,
    user_id,
    total_comments,
    class_topic_id,
    ...rest
  } = props;
  const dispatch = useAppDispatch();
  const [editId, setEditId] = useState<string | undefined>(undefined);
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const { Confirm, open } = useConfirm();
  const currentUser = useAppSelector((state) => state.ui.app.me);
  const isAdmin = useAppSelector(isAdminSelector);

  const replies = useAppSelector((state) =>
    selectHomeworkReplyComments(state, rest.id),
  );

  const navigate = useNavigate();
  const { pathname, state } = useLocation();

  useEffect(() => {
    navigate(pathname, { state: undefined });
  }, []);

  useEffect(() => {
    if (state) {
      const { user_assignment_file_comment_id } = state as any;
      const element = document.getElementById(user_assignment_file_comment_id);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [state]);

  return (
    // parent comment
    <Comments
      id={rest.id}
      name={user?.name}
      userId={user?.id}
      profileUrl={user?.profile_picture}
      date={created_at}
      numberOfComments={total_comments}
      onEdit={
        user_id === currentUser?.id ? () => setEditId(rest.id) : undefined
      }
      onDelete={
        user_id === currentUser?.id || isAdmin
          ? () => {
              open({
                title: 'Confirm delete!',
                content: 'Are you sure to delete this comment?',
                async onOk() {
                  await dispatch(deleteHomeworkComment(props));
                },
              });
            }
          : undefined
      }
      onReply={() => {
        setParentId((i) => (i === rest.id ? undefined : rest.id));
      }}
      text={
        <React.Fragment>
          {editId === rest.id ? (
            <ChatBox
              placeholder="Write a reply"
              html={message}
              onEsc={() => setEditId(undefined)}
              onSend={async (html) => {
                setEditId(undefined);
                dispatch(
                  homeworkCommentActions.updateEntity({
                    ...props,
                    message: html!,
                  }),
                );

                await dispatch(
                  updateHomeworkComment({
                    ...rest,
                    user_id: user?.id,
                    message: html,
                  }),
                );
              }}
            />
          ) : (
            htmlParser(message || '')
          )}
          <Confirm />
        </React.Fragment>
      }
      onComment={async () => {
        await dispatch(
          fetchHomeworkCommentReply({
            parent_id: rest.id,
            class_topic_id: class_topic_id,
          }),
        );
      }}
    >
      {!!replies?.length &&
        replies.map((r) => {
          // child comment
          return (
            <Comments
              id={r.id}
              key={r.id}
              name={r.user.name}
              userId={r.user_id}
              profileUrl={r.user.profile_picture}
              date={r.created_at}
              onDelete={
                r.user_id === currentUser?.id || isAdmin
                  ? () => {
                      open({
                        title: 'Confirm delete!',
                        content: 'Are you sure to delete this comment?',
                        async onOk() {
                          await dispatch(deleteHomeworkCommentReply(r));
                        },
                      });
                    }
                  : undefined
              }
              onEdit={
                r.user_id === currentUser?.id
                  ? () => {
                      setEditId(r.id);
                    }
                  : undefined
              }
              text={
                editId === r.id ? (
                  <ChatBox
                    placeholder="Write a reply"
                    html={r.message}
                    onEsc={() => setEditId(undefined)}
                    onSend={(html) => {
                      setEditId(undefined);
                      dispatch(
                        homeworkReplyCommentActions.updateEntity({
                          ...r,
                          message: html,
                        }),
                      );

                      dispatch(
                        updateHomeworkCommentReply({ ...r, message: html }),
                      );
                    }}
                  />
                ) : (
                  htmlParser(r.message || '')
                )
              }
            />
          );
        })}

      {parentId === rest.id && (
        <Comments
          name={currentUser?.name}
          userId={currentUser?.id}
          profileUrl={currentUser?.profile_picture}
          text={
            <ChatBox
              shouldFocus
              placeholder="Write a reply"
              onEsc={() => setParentId(undefined)}
              onSend={async (html) => {
                dispatch(
                  homeworkReplyCommentActions.createDummy({
                    user_id: currentUser?.id,
                    class_topic_id,
                    user_assignment_file_id: rest.user_assignment_file_id,
                    message: html,
                    parent_id: parentId,
                    user: currentUser!,
                  }),
                );

                dispatch(
                  createHomeworkCommentReply({
                    user_id: currentUser?.id,
                    class_topic_id,
                    user_assignment_file_id: rest.user_assignment_file_id,
                    message: html,
                    parent_id: parentId,
                  }),
                );
                setParentId(undefined);
              }}
            />
          }
        />
      )}
    </Comments>
  );
};

export default HomeworkReply;
